import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { ApiError } from "@/api/client";
import { fetchNitrateNitriteOverview } from "@/api/endpoints";
import { InfoTip } from "@/components/InfoTip";
import { useAuth } from "@/hooks/useAuth";
import { formatDate, formatRelativeDate } from "@/lib/format";
import { TIPS } from "@/lib/glossary";
import { usePageTitle } from "@/hooks/usePageTitle";
import type {
  ComplianceState,
  NitrateNitriteCounts,
  PWSNitrateNitrite,
} from "@/types/api";

type StateFilter = "all" | ComplianceState | "unknown";

const STATE_PILL: Record<ComplianceState, string> = {
  red: "pill-high",
  due: "pill-low",
  orange: "pill-medium",
  green: "pill-ok",
};

const STATE_LABEL: Record<ComplianceState, string> = {
  red: "OUT OF COMPLIANCE",
  due: "NOW DUE",
  orange: "TEST FAILED",
  green: "COMPLIANT",
};

function pwsState(row: PWSNitrateNitrite): StateFilter {
  return row.status?.state ?? "unknown";
}

// Most recent last_test_date among a tenant's PWS — "when did anyone in this
// tenant last test?". Null if every PWS lacks a test date.
function mostRecentLastTest(rows: PWSNitrateNitrite[]): string | null {
  let latest: string | null = null;
  for (const r of rows) {
    const d = r.status?.last_test_date;
    if (!d) continue;
    if (latest === null || d > latest) latest = d;
  }
  return latest;
}

export function NitrateNitriteOverview(): JSX.Element {
  usePageTitle("Nitrate / Nitrite compliance");
  const auth = useAuth();
  const [params, setParams] = useSearchParams();
  const filter = (params.get("state") ?? "all") as StateFilter;
  const tenantFilter = params.get("tenant");
  const [tenantSearch, setTenantSearch] = useState("");

  const query = useQuery({
    queryKey: ["compliance-nn-overview"],
    queryFn: fetchNitrateNitriteOverview,
    refetchInterval: 60 * 60 * 1000,
  });

  const filteredSystems = useMemo(() => {
    const data = query.data;
    if (!data) return [];
    return data.systems.filter((r) => {
      if (filter !== "all" && pwsState(r) !== filter) return false;
      if (tenantFilter && r.tenant_id !== tenantFilter) return false;
      return true;
    });
  }, [query.data, filter, tenantFilter]);

  // Group systems by tenant for per-tenant rendering. We do this client-side
  // (rather than reading the backend's by_tenant counts) so the same data
  // structure powers the "oldest test" column and pill filter behavior.
  const tenantGroups = useMemo(() => {
    const data = query.data;
    if (!data || !data.by_tenant) return [];
    const byId = new Map<string, PWSNitrateNitrite[]>();
    for (const s of data.systems) {
      if (!s.tenant_id) continue;
      const list = byId.get(s.tenant_id) ?? [];
      list.push(s);
      byId.set(s.tenant_id, list);
    }
    return data.by_tenant.map((t) => {
      const rows = byId.get(t.tenant_id) ?? [];
      return {
        tenant_id: t.tenant_id,
        tenant_name: t.tenant_name,
        counts: t.counts,
        rows,
        last_test_date: mostRecentLastTest(rows),
      };
    });
  }, [query.data]);

  function setFilter(next: StateFilter): void {
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      if (next === "all") p.delete("state");
      else p.set("state", next);
      return p;
    });
  }

  function setTenantFilter(id: string | null): void {
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      if (id === null) p.delete("tenant");
      else p.set("tenant", id);
      return p;
    });
  }

  function clearAllFilters(): void {
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete("state");
      p.delete("tenant");
      return p;
    });
  }

  if (query.isLoading) {
    return (
      <div className="space-y-4">
        <div className="skeleton h-24" aria-label="Loading" />
        <div className="skeleton h-64" aria-label="Loading systems" />
      </div>
    );
  }

  if (query.error) {
    const msg =
      query.error instanceof ApiError ? query.error.message : "Failed to load";
    return <p className="text-sm text-severity-high">{msg}</p>;
  }

  const data = query.data!;
  const isPlatform = auth.scope === "platform";
  const filterActive = filter !== "all" || tenantFilter !== null;

  return (
    <div className="space-y-6">
      <header className="flex items-baseline justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold inline-flex items-center">
            Nitrate / Nitrite compliance
            <InfoTip {...TIPS.nnStates} />
          </h1>
          <p className="text-sm text-ink-300">
            Calendar year {data.year} · {data.aggregate.total}{" "}
            {data.aggregate.total === 1 ? "system" : "systems"}
            <InfoTip {...TIPS.calendarYear} />
          </p>
        </div>
        {filterActive && (
          <button
            type="button"
            className="btn-primary py-1 px-2 text-xs"
            onClick={clearAllFilters}
          >
            Clear filters
          </button>
        )}
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <StatCard
          label="Out of compliance"
          count={data.aggregate.red}
          total={data.aggregate.total}
          tone="high"
          active={filter === "red"}
          onClick={() => setFilter(filter === "red" ? "all" : "red")}
        />
        <StatCard
          label="Now due"
          count={data.aggregate.due}
          total={data.aggregate.total}
          tone="low"
          active={filter === "due"}
          onClick={() => setFilter(filter === "due" ? "all" : "due")}
        />
        <StatCard
          label="Test failed"
          count={data.aggregate.orange}
          total={data.aggregate.total}
          tone="medium"
          active={filter === "orange"}
          onClick={() => setFilter(filter === "orange" ? "all" : "orange")}
        />
        <StatCard
          label="Compliant"
          count={data.aggregate.green}
          total={data.aggregate.total}
          tone="ok"
          active={filter === "green"}
          onClick={() => setFilter(filter === "green" ? "all" : "green")}
        />
        <StatCard
          label="Unknown"
          count={data.aggregate.unknown}
          total={data.aggregate.total}
          tone="muted"
          active={filter === "unknown"}
          onClick={() => setFilter(filter === "unknown" ? "all" : "unknown")}
        />
      </div>

      {isPlatform && tenantGroups.length > 0 && (
        <PerTenantTable
          groups={tenantGroups}
          stateFilter={filter}
          tenantFilter={tenantFilter}
          tenantSearch={tenantSearch}
          onTenantSearch={setTenantSearch}
          onTenantFilter={setTenantFilter}
        />
      )}

      <SystemsTable
        rows={filteredSystems}
        isPlatform={isPlatform}
        filterActive={filterActive}
        onClearTenant={tenantFilter ? () => setTenantFilter(null) : null}
      />
    </div>
  );
}

interface StatCardProps {
  label: string;
  count: number;
  total: number;
  tone: "high" | "medium" | "low" | "ok" | "muted";
  active: boolean;
  onClick: () => void;
}

function StatCard({ label, count, total, tone, active, onClick }: StatCardProps): JSX.Element {
  const toneClass = {
    high: "text-severity-high",
    medium: "text-severity-medium",
    low: "text-severity-low",
    ok: "text-severity-ok",
    muted: "text-ink-400",
  }[tone];
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`card text-left transition-colors ${active ? "ring-2 ring-ink-200" : "hover:bg-ink-700/50"}`}
    >
      <div className="text-xs uppercase tracking-wide text-ink-300">{label}</div>
      <div className={`text-3xl font-semibold mt-1 ${toneClass}`}>{count}</div>
      <div className="text-xs text-ink-400 mt-1">{pct}% of total</div>
    </button>
  );
}

interface CountPillsProps {
  counts: NitrateNitriteCounts;
  filter: StateFilter;
}

// Colored chips for the non-zero states. When a state filter is active,
// only renders the pill matching that filter — keeps the column quiet
// and consistent with the systems-list filter.
function CountPills({ counts, filter }: CountPillsProps): JSX.Element {
  const items: { state: StateFilter; n: number; cls: string; label: string }[] = [
    { state: "red", n: counts.red, cls: "pill-high", label: "out of compliance" },
    { state: "due", n: counts.due, cls: "pill-low", label: "now due" },
    { state: "orange", n: counts.orange, cls: "pill-medium", label: "test failed" },
    { state: "green", n: counts.green, cls: "pill-ok", label: "compliant" },
    { state: "unknown", n: counts.unknown, cls: "pill", label: "unknown" },
  ];
  const visible = items.filter((it) => it.n > 0 && (filter === "all" || filter === it.state));
  if (visible.length === 0) return <span className="text-ink-400 text-xs">—</span>;
  return (
    <div className="inline-flex flex-wrap items-center gap-1.5">
      {visible.map((it) => (
        <span key={it.state} className={it.cls} title={`${it.n} ${it.label}`}>
          {it.n}
        </span>
      ))}
    </div>
  );
}

interface TenantGroup {
  tenant_id: string;
  tenant_name: string;
  counts: NitrateNitriteCounts;
  rows: PWSNitrateNitrite[];
  last_test_date: string | null;
}

interface PerTenantTableProps {
  groups: TenantGroup[];
  stateFilter: StateFilter;
  tenantFilter: string | null;
  tenantSearch: string;
  onTenantSearch: (s: string) => void;
  onTenantFilter: (id: string | null) => void;
}

function PerTenantTable({
  groups,
  stateFilter,
  tenantFilter,
  tenantSearch,
  onTenantSearch,
  onTenantFilter,
}: PerTenantTableProps): JSX.Element {
  const visible = groups.filter((t) => {
    if (tenantSearch && !t.tenant_name.toLowerCase().includes(tenantSearch.toLowerCase()))
      return false;
    if (stateFilter !== "all") {
      const n =
        stateFilter === "red"
          ? t.counts.red
          : stateFilter === "due"
            ? t.counts.due
            : stateFilter === "orange"
              ? t.counts.orange
              : stateFilter === "green"
                ? t.counts.green
                : t.counts.unknown;
      if (n === 0) return false;
    }
    return true;
  });

  return (
    <section className="card overflow-x-auto">
      <header className="flex items-baseline justify-between gap-3 mb-2">
        <h2 className="card-title mb-0">By tenant ({visible.length})</h2>
        <input
          type="text"
          placeholder="Search tenants…"
          className="input max-w-xs text-sm"
          value={tenantSearch}
          onChange={(e) => onTenantSearch(e.target.value)}
        />
      </header>
      {visible.length === 0 ? (
        <p className="text-sm text-ink-300">No tenants match the current filter.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left text-ink-300 border-b border-ink-700">
            <tr>
              <th className="py-2 pr-4 font-medium">Tenant<InfoTip {...TIPS.tenant} /></th>
              <th className="py-2 pr-4 font-medium">Status<InfoTip {...TIPS.nnStates} /></th>
              <th className="py-2 pr-4 font-medium">Last test</th>
              <th className="py-2 pr-4 font-medium text-right">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-700">
            {visible.map((t) => {
              const isActive = tenantFilter === t.tenant_id;
              return (
                <tr
                  key={t.tenant_id}
                  className={isActive ? "bg-ink-700/40" : ""}
                >
                  <td className="py-2 pr-4">
                    <button
                      type="button"
                      onClick={() => onTenantFilter(isActive ? null : t.tenant_id)}
                      title={
                        isActive
                          ? "Clear tenant filter"
                          : "Filter systems list to this tenant"
                      }
                      className={`text-left hover:text-ink-100 ${
                        isActive ? "text-ink-100 underline" : "text-ink-200 hover:underline"
                      }`}
                    >
                      {t.tenant_name}
                      {isActive && (
                        <span className="ml-2 text-xs text-ink-400">
                          (filtered · click to clear)
                        </span>
                      )}
                    </button>
                  </td>
                  <td className="py-2 pr-4">
                    <CountPills counts={t.counts} filter={stateFilter} />
                  </td>
                  <td className="py-2 pr-4 text-ink-300 whitespace-nowrap">
                    {t.last_test_date ? (
                      <>
                        {formatDate(t.last_test_date)}
                        <span className="ml-2 text-xs text-ink-400">
                          {formatRelativeDate(t.last_test_date)}
                        </span>
                      </>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="py-2 pr-4 text-right font-mono">{t.counts.total}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

interface SystemsTableProps {
  rows: PWSNitrateNitrite[];
  isPlatform: boolean;
  filterActive: boolean;
  onClearTenant: (() => void) | null;
}

function SystemsTable({
  rows,
  isPlatform,
  filterActive,
  onClearTenant,
}: SystemsTableProps): JSX.Element {
  return (
    <section className="card overflow-x-auto">
      <header className="flex items-baseline justify-between mb-3">
        <h2 className="card-title mb-0">Systems ({rows.length})</h2>
        {onClearTenant && (
          <button
            type="button"
            className="text-xs text-ink-300 underline"
            onClick={onClearTenant}
          >
            Clear tenant filter
          </button>
        )}
      </header>
      {rows.length === 0 ? (
        <p className="text-sm text-ink-300">
          {filterActive
            ? "No systems match the current filter."
            : "No systems on file."}
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left text-ink-300 border-b border-ink-700">
            <tr>
              <th className="py-2 pr-4 font-medium">PWS ID<InfoTip {...TIPS.pwsId} /></th>
              <th className="py-2 pr-4 font-medium">Name</th>
              {isPlatform && <th className="py-2 pr-4 font-medium">Tenant<InfoTip {...TIPS.tenant} /></th>}
              <th className="py-2 pr-4 font-medium">State<InfoTip {...TIPS.nnStates} /></th>
              <th className="py-2 pr-4 font-medium">Last test</th>
              <th className="py-2 font-medium" />
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-700">
            {rows.map((r) => {
              const s = r.status;
              const last = s?.last_test_date ?? null;
              return (
                <tr key={r.pws_id}>
                  <td className="py-2 pr-4 font-mono text-xs">{r.pws_id}</td>
                  <td className="py-2 pr-4">{r.pws_name}</td>
                  {isPlatform && (
                    <td className="py-2 pr-4 text-ink-300">{r.tenant_name ?? "—"}</td>
                  )}
                  <td className="py-2 pr-4">
                    {s ? (
                      <span className={STATE_PILL[s.state]}>{STATE_LABEL[s.state]}</span>
                    ) : (
                      <span className="pill" title={r.error ?? undefined}>
                        UNKNOWN
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-4 text-ink-300 whitespace-nowrap">
                    {last ? (
                      <>
                        {formatDate(last)}
                        <span className="ml-2 text-xs text-ink-400">
                          {formatRelativeDate(last)}
                        </span>
                      </>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="py-2">
                    <Link
                      to={`/systems/${encodeURIComponent(r.pws_id)}`}
                      className="btn-primary py-1 px-2 text-xs"
                    >
                      Open
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}
